import { useEffect } from "react";
import { PRIMARY_CTAS, type PrimaryCTA } from "@/content/primary-cta";

/**
 * Per-route configuration for the mobile sticky CTA bar.
 *
 * Two layers, merged in this order (later wins):
 *   1. `DEFAULT_STICKY_CTA_CONFIG`         — global defaults.
 *   2. `STICKY_CTA_ROUTE_CONFIG`           — static overrides keyed by
 *                                            pathname (exact match first,
 *                                            then longest matching prefix
 *                                            ending in `*`).
 *   3. `useStickyCTAConfig({ path, ... })` — runtime overrides published
 *                                            by a page/section while it
 *                                            is mounted. Use this for
 *                                            in-page control (e.g. hide
 *                                            the bar around a hero video
 *                                            or a pricing table without
 *                                            sprinkling `data-sticky-cta`
 *                                            attributes everywhere).
 *
 * Every field is optional; unset fields fall through to the previous
 * layer. This lets each page override only what it cares about.
 */
export type StickyCTASlot = PrimaryCTA;

/**
 * Catalog of every sticky CTA button the bar can render.
 *
 * Page-primary slots are spread in from `PRIMARY_CTAS` so the sticky bar's
 * text and destination are byte-identical to the hero, mid-page and final
 * CTA on the same route — at every breakpoint. Never redefine those labels
 * here.
 */
export const STICKY_CTA_SLOTS: Record<string, StickyCTASlot> = {
  [PRIMARY_CTAS.home.id]: PRIMARY_CTAS.home,
  [PRIMARY_CTAS.masterclass.id]: PRIMARY_CTAS.masterclass,
  [PRIMARY_CTAS.advocacy.id]: PRIMARY_CTAS.advocacy,
  // Same words, same booker — but the advocacy sub-pages don't host the
  // #book anchor, so the destination is resolved to the /advocacy page.
  primary_cta_advocacy_offsite: {
    ...PRIMARY_CTAS.advocacy,
    id: "primary_cta_advocacy_offsite",
    href: `/advocacy${PRIMARY_CTAS.advocacy.href}`,
  },
  sticky_cta_scan: {
    id: "sticky_cta_scan",
    kind: "registration",
    href: "/scan",
    label: "Free Scan",
    action: "Free Scan. Opens the free readiness scan.",
  },
  sticky_cta_inquire: {
    id: "sticky_cta_inquire",
    kind: "strategy_session",
    href: "/inquire",
    label: "Apply for Coaching",
    action: "Apply for Coaching. Opens the coaching application.",
  },
  // NOTE: there is no free masterclass. The masterclass seat is paid
  // ($297) — the free top-of-funnel offer is the Make Change Scan
  // (`PRIMARY_CTAS.home`). Never reintroduce a "free masterclass" label.

};

/**
 * Site-wide default: the masterclass seat, worded exactly as the masterclass
 * hero/band words it.
 */
export const DEFAULT_STICKY_CTA_SLOT_IDS = [PRIMARY_CTAS.masterclass.id];

export type StickyCTAConfig = {
  /** Which slots (from STICKY_CTA_SLOTS) to render, in order. */
  slots?: string[];
  /**
   * Pin the bar open: skip hero gating, scroll thresholds, scroll-up
   * hiding and the near-footer hide. The bar still yields to an open
   * keyboard, explicit suppressors, and the Escape dismissal.
   */
  alwaysVisible?: boolean;
  /** Fully skip the bar on this route (equivalent to a suppressed path). */
  disabled?: boolean;
  /** Absolute minimum scroll depth (px) before the bar can appear. */
  thresholdPx?: number;
  /** Proportional threshold as a fraction of viewport height (0–1). */
  thresholdVh?: number;
  /** Distance from doc bottom (px) at which the bar hides. */
  nearFooterPx?: number;
  /**
   * Depth (as a fraction of viewport height) after which the bar stays
   * visible even when the user isn't scrolling downward. Higher = the
   * bar re-hides more aggressively.
   */
  persistAfterVh?: number;
  /** Hide the bar while scrolling upward. */
  hideOnScrollUp?: boolean;
  /**
   * Reported to layouts as `--sticky-cta-height` on <body>. Pages can
   * override to reserve additional space for their own fixed elements.
   * Ignored when the bar isn't visible.
   */
  minHeightPx?: number;
};

export const DEFAULT_STICKY_CTA_CONFIG: Required<
  Omit<StickyCTAConfig, "disabled" | "minHeightPx" | "slots" | "alwaysVisible">
> & {
  disabled: boolean;
  minHeightPx: number | null;
  slots: string[];
  alwaysVisible: boolean;
} = {
  disabled: false,
  slots: DEFAULT_STICKY_CTA_SLOT_IDS,
  // Pinned open site-wide: the masterclass registration CTA is the
  // primary conversion goal, so it stays reachable at any scroll depth.
  // Readability is protected by <body> padding-bottom
  // (--sticky-cta-height) plus the keyboard/suppressor guards.
  alwaysVisible: true,
  thresholdPx: 480,
  thresholdVh: 0.4,
  nearFooterPx: 320,
  persistAfterVh: 1.25,
  hideOnScrollUp: true,
  minHeightPx: null,
};

/**
 * Static, checked-in per-route defaults. Keys are either an exact
 * pathname or a prefix ending in `*` (longest prefix wins). Pages that
 * need dynamic control should use `useStickyCTAConfig` instead.
 */
export const STICKY_CTA_ROUTE_CONFIG: Record<string, StickyCTAConfig> = {
  // Form-first routes — the page owns the bottom edge.
  "/inquire": { disabled: true },
  "/scan": { disabled: true },
  "/free": { disabled: true },
  "/links": { disabled: true },
  "/auth": { disabled: true },
  "/book*": { disabled: true },

  // Masterclass — a single, always-visible registration CTA. This is the
  // page's only conversion goal, so the bar is pinned open from the top
  // of the page instead of appearing on scroll.
  "/": { slots: [PRIMARY_CTAS.home.id], alwaysVisible: true },

  "/masterclass*": {
    // The masterclass seat itself is paid ($297) and already has hero,
    // mid-page and final-band buttons. The sticky bar carries the FREE
    // offer instead — the Make Change Scan — so its label never
    // contradicts the on-page pricing.
    slots: [PRIMARY_CTAS.home.id],
    alwaysVisible: true,
    thresholdPx: 0,
    thresholdVh: 0,
    nearFooterPx: 0,
    hideOnScrollUp: false,
  },


  // Advocacy funnel — surface the bar earlier so pricing readers see it,
  // and pull it up higher above the footer's own CTA stack.
  // /advocacy renders its own in-page mobile bar with the same
  // "Book a Discovery Call" CTA, so the global bar stands down there.
  "/advocacy": { disabled: true },
  "/advocacy/pricing": { slots: ["primary_cta_advocacy_offsite"], thresholdVh: 0.2, nearFooterPx: 560 },
  "/advocacy/how-it-works": { slots: ["primary_cta_advocacy_offsite"], thresholdVh: 0.3, nearFooterPx: 420 },

  // Long-form editorial — don't nag until the reader is well into it.
  "/blog*": { thresholdVh: 0.75, thresholdPx: 900 },

  // QA tooling — always hidden so it can't interfere with screenshots.
  "/qa*": { disabled: true },
};

const matchRouteConfig = (pathname: string): StickyCTAConfig => {
  if (STICKY_CTA_ROUTE_CONFIG[pathname]) return STICKY_CTA_ROUTE_CONFIG[pathname];
  let best: { key: string; cfg: StickyCTAConfig } | null = null;
  for (const [key, cfg] of Object.entries(STICKY_CTA_ROUTE_CONFIG)) {
    if (!key.endsWith("*")) continue;
    const prefix = key.slice(0, -1);
    if (pathname.startsWith(prefix)) {
      if (!best || prefix.length > best.key.length - 1) {
        best = { key, cfg };
      }
    }
  }
  return best?.cfg ?? {};
};

// ------------------------------------------------------------------
// Runtime override store — a tiny pub/sub keyed by path so a page can
// publish its own config for as long as it's mounted. This avoids
// wrapping the whole app in a Provider just for a mobile bar.
// ------------------------------------------------------------------

type Listener = () => void;
const runtimeOverrides = new Map<string, StickyCTAConfig>();
const runtimeListeners = new Set<Listener>();
let overrideVersion = 0;

const notify = () => {
  overrideVersion += 1;
  for (const l of runtimeListeners) l();
};

/**
 * Publish a runtime override for a given pathname while the calling
 * component is mounted. Cleared automatically on unmount. If two
 * components register overrides for the same path, the most recently
 * mounted wins — keep this to one caller per route.
 */
export function useStickyCTAConfig(
  path: string,
  config: StickyCTAConfig,
): void {
  const serialized = JSON.stringify(config);
  useEffect(() => {
    runtimeOverrides.set(path, JSON.parse(serialized));
    notify();
    return () => {
      runtimeOverrides.delete(path);
      notify();
    };
  }, [path, serialized]);
}

export function getStickyCTAConfig(pathname: string) {
  const merged = {
    ...DEFAULT_STICKY_CTA_CONFIG,
    ...matchRouteConfig(pathname),
    ...(runtimeOverrides.get(pathname) ?? {}),
  };
  return merged;
}

export function subscribeStickyCTAConfig(listener: Listener) {
  runtimeListeners.add(listener);
  return () => {
    runtimeListeners.delete(listener);
  };
}

export function getStickyCTAConfigVersion() {
  return overrideVersion;
}
